import { BrowserInfo, detect } from 'detect-browser';
import isUndefined from 'lodash-es/isUndefined';
import map from 'lodash-es/map';
import isString from 'lodash-es/isString';
import difference from 'lodash-es/difference';
import isEmpty from 'lodash-es/isEmpty';
import includes from 'lodash-es/includes';

class BrowserHelper {
  window: any;
  private languagePreferred: any;

  constructor() {
    this.window = window;
  }

  // to mock window in tests
  setWindow(win: Window): void {
    this.window = win;
  }

  // Safari, in Private Browsing Mode, looks like it supports localStorage but all calls to localStorage.setItem
  // throw QuotaExceededError. We're going to detect this and just silently drop any calls to localStorage.setItem
  // inside the Storage service.
  get areCookiesAvailable(): boolean {
    this.window.document.cookie = '__verify=1';
    let available = this.window.document.cookie.length >= 1 && this.window.document.cookie.indexOf('__verify=1') !== -1;

    const cookieExpireDate = new Date(1976, 8, 16).toUTCString();
    this.window.document.cookie = `__verify=1;expires=${cookieExpireDate}`;

    // Safari in private mode does not allow cookies, while all APIs and checks are successful
    if (this.isSafari) {
      available = this.isLocalStorageAvailable ? available : false;
    }

    return available;
  }

  get isInappBrowser(): boolean {
    const { userAgent } = this.window.navigator;
    const isExternalInApp = /WKWebView/i.test(userAgent) && !this.isInWrapper;
    return /Twitter/i.test(userAgent) || /FBAN/i.test(userAgent) || /LinkedIn/i.test(userAgent) || isExternalInApp;
  }

  get isInIframe(): boolean {
    try {
      return this.window.self !== this.window.top;
    } catch (e) {
      return true;
    }
  }

  // set in the ts-mobile.html used inside the wrapper
  get isInWrapper(): boolean {
    return this.window.IS_IN_WRAPPER;
  }

  get isIos(): boolean {
    // Excludes IE pretending it's iPhone https://stackoverflow.com/a/9039885
    return /iPad|iPhone|iPod/.test(this.window.navigator.userAgent) && !this.window.MSStream;
  }

  get isLocalhost(): boolean {
    // 9876 is karma runner port
    return this.window.location.hostname === 'localhost' && this.window.location.port !== '9876';
  }

  get isLocalStorageAvailable(): boolean {
    let available = true;

    try {
      if (typeof this.window.localStorage === 'object') {
        this.window.localStorage.setItem('localStorage', '1');
        this.window.localStorage.removeItem('localStorage');
      } else {
        available = false;
      }
    } catch (e) {
      available = false;
    }

    return available;
  }

  get isMac(): boolean {
    return /Mac/i.test(this.window.navigator.userAgent);
  }

  get isMobile(): boolean {
    return (
      /Android/i.test(this.window.navigator.userAgent) ||
      /webOS/i.test(this.window.navigator.userAgent) ||
      /BlackBerry/i.test(this.window.navigator.userAgent) ||
      /Windows Phone/i.test(this.window.navigator.userAgent) ||
      this.isIos
    );
  }

  get isSafari(): boolean {
    const browserRegexp = /^((?!chrome|android).)*safari/i;
    return browserRegexp.test(this.window.navigator.userAgent);
  }

  get deviceInfo(): string {
    const browser = detect() as BrowserInfo;
    return `${browser.os}, ${browser.name}:${browser.version}`;
  }

  // Protocol is either 'http:' or 'https:'
  get isSecure(): boolean {
    return this.window.location.protocol === 'https:';
  }

  get preferredLanguage(): string {
    if (this.languagePreferred) {
      return this.languagePreferred;
    }

    return this.window.navigator.languages ? this.window.navigator.languages[0] : this.legacyPreferredLanguage;
  }

  get legacyPreferredLanguage(): string {
    const navigator = this.window.navigator as any;
    return navigator.language || navigator.browserLanguage || navigator.systemLanguage || navigator.userLanguage;
  }

  get browserUrl(): string {
    if (this.isCname()) {
      return this.window.location.protocol + '//' + this.window.location.hostname;
    }
    if (this.subdomain()) {
      const host = this.window.location.host.split('.')[1];
      return this.window.location.protocol + '//' + this.subdomain() + '.' + host + '.com';
    }

    return '';
  }

  // @NB even if Web Share API is supported by browser,
  // navigator.share will be defined only on domains with https on
  // https://developer.mozilla.org/en-US/docs/Web/API/Navigator/share
  get isWebShareApiAvailable(): boolean {
    const navigator = this.window.navigator as any;
    return !isUndefined(navigator.share);
  }

  // Methods
  // =======

  forceLocaleRecalculation(banned: string[]): string {
    const allowedLocale = this.getLegalLocale(banned);
    this.languagePreferred = allowedLocale;
    return allowedLocale;
  }

  formatLocales(locales: string[]): string[] {
    return map(locales, locale => {
      return locale.split('_')[0];
    });
  }

  formatLocaleString(locale: string): string {
    if (isString(locale)) {
      const parts = locale.split('_');
      if (parts[1]) {
        parts[1] = parts[1].toUpperCase();
      }
      locale = parts.join('_');
    }

    return locale;
  }

  getLegalLocale(banned: string[]): string {
    const navigatorLocales = this.window.navigator.languages
      ? this.formatLocales(this.window.navigator.languages as string[])
      : [];
    const bannedLocales = this.formatLocales(banned);
    const availableLocales = difference(navigatorLocales, bannedLocales);

    const selectedLocale = !isEmpty(availableLocales) ? availableLocales[0] : this.legacyPreferredLanguage;

    return includes(bannedLocales, selectedLocale) ? 'en_US' : selectedLocale;
  }

  redirectTo(uri: string): void {
    if (this.isInWrapper) {
      // HTML5 mode off
      this.window.location.hash = '#' + uri;
    } else {
      // HTML5 mode on
      this.window.location.href = uri;
    }
  }

  reloadPage(): void {
    this.window.location.reload();
  }

  public subdomain(): string {
    if (this.isCname()) {
      return this.window.localStorage.getItem('companySubdomain');
    }

    if (this.hasSubdomain(this.window.location.host)) {
      const subdomain = this.window.location.host.split('.')[0];
      return subdomain;
    } else {
      return '';
    }
  }

  public hasSubdomain(host: string): boolean {
    const locations = host.split('.');

    // 'feature-server.qa-1brd.com' is the branch specific test environment.
    const isFeatureEnv = host.indexOf('feature-server.qa-1brd.com') > -1;
    const isFeaturesWithSubdomain = isFeatureEnv && locations.length > 4;

    // Other environments are always length 2: ENVIRONMENT.com (qa-1brd.com, staging-1brd.com, 1brd.com)
    const isOtherWithSubdomain = !isFeatureEnv && locations.length > 2;

    return isFeaturesWithSubdomain || isOtherWithSubdomain;
  }

  public isCname(): boolean {
    return !this.window.location.host.includes('1brd') && !this.isInWrapper;
  }
}

export default new BrowserHelper();
